import React, { useCallback, useState, useEffect, useRef } from 'react';
import { CRow, CCol } from '@coreui/react-pro';
import moment from 'moment';
import { useAppDispatch, useAppSelector } from '../../app/hooks';
import { selectApp, setAppFlags } from '../../slice/appSlice';
import {
  selectSim,
  clearSimOFPForm,
  setSimOFPFormInEdit,
  setAdditionalInfoForm,
  addCrews,
  deleteCrew,
  setDutyCode,
} from '../../slice/simSlice';
import {
  CXLightBox,
  CXDropDown,
  CXDatePicker,
  CXTextInput,
  CXButton,
  CXTextArea,
} from '../CXForm';
import { selectAuth } from '../../slice/authSlice';
import {
  postNewSimPlanUpdate,
  getAircraftTypes,
  getSimPlanTemplates,
  getSimTemplateUfiList,
  getSimTemplateTagList,
} from '../../services/simAPI';
import SimTraineeInfoListItem from './SimTraineeInfoListItem';
import {
  characterOnlyInput,
  numberOnlyInput,
  numberOnlyInputOrNull,
} from '../../utils/formValidateHelper';
import SingleSelect from '../SingleSelect';
import './MultiSelect.scss';

interface IAddOrEditOFPLightBox {
  postAction?: Function;
  isEdit?: boolean;
  heading: string;
  submitButtonText: string;
  TriggerButton: JSX.Element;
  allTemplateUfis?: string[];
  templateTags?: string[];
  tagUfiMap?: Record<string, string[]>;
  enablePagedTemplateSelector?: boolean;
  initialTemplateUfiPaged?: IPagedStringList;
  initialTemplateTagPaged?: IPagedStringList;
}

const rowStyle = { paddingTop: '24px', paddingBottom: '24px' };
const DEFAULT_PAGE_LIMIT = 25;
const PAGED_SELECT_DEBOUNCE_MS = 600;
const normalizeTagKey = (tag?: string) => (String(tag || 'ALL').trim() || 'ALL');

interface IPagedStringList {
  records: string[];
  page: number;
  limit: number;
  total: number;
  totalPages: number;
}

const DEFAULT_PAGED_STRING_LIST: IPagedStringList = {
  records: [],
  page: 1,
  limit: DEFAULT_PAGE_LIMIT,
  total: 0,
  totalPages: 1,
};

const normalizePagedStringList = (res: any, fallback: IPagedStringList): IPagedStringList => {
  const data = res?.data ?? res ?? {};
  const records = data?.records ?? data?.data?.records ?? data?.tags ?? data?.ufis ?? [];
  const page = Number(data?.page ?? data?.data?.page ?? fallback.page);
  const limit = Number(data?.limit ?? data?.data?.limit ?? fallback.limit);
  const total = Number(data?.total ?? data?.data?.total ?? fallback.total);
  const totalPages = Number(data?.totalPages ?? data?.data?.totalPages ?? fallback.totalPages);

  return {
    records: Array.isArray(records) ? records.map((v) => String(v || '').trim()).filter(Boolean) : [],
    page: Number.isFinite(page) && page > 0 ? page : fallback.page,
    limit: Number.isFinite(limit) && limit > 0 ? limit : fallback.limit,
    total: Number.isFinite(total) && total >= 0 ? total : fallback.total,
    totalPages: Number.isFinite(totalPages) && totalPages > 0 ? totalPages : fallback.totalPages,
  };
};

const mergeUniqueStrings = (base: string[], incoming: string[]) => {
  const merged = Array.isArray(base) ? [...base] : [];
  const seen = new Set(merged);
  (Array.isArray(incoming) ? incoming : []).forEach((item) => {
    const value = String(item || '').trim();
    if (!value || seen.has(value)) return;
    seen.add(value);
    merged.push(value);
  });
  return merged;
};

const isFailedResponse = (res: any) => {
  const status = Number(res?.status ?? res?.data?.status);
  if (Number.isFinite(status) && status >= 400) return true;
  if (res?.data?.errors || res?.errors) return true;
  if (res?.name === 'serverError') return true;
  return false;
};

const isCanceledResponse = (res: any) =>
  res?.code === 'ERR_CANCELED' ||
  res?.name === 'CanceledError';

const AddOrEditOFPLightBox = ({
  postAction,
  isEdit = false,
  heading,
  submitButtonText,
  TriggerButton,
  allTemplateUfis = [],
  templateTags = [],
  tagUfiMap = {},
  enablePagedTemplateSelector = false,
  initialTemplateUfiPaged = DEFAULT_PAGED_STRING_LIST,
  initialTemplateTagPaged = DEFAULT_PAGED_STRING_LIST,
}: IAddOrEditOFPLightBox) => {
  const dispatch = useAppDispatch();
  const { isAddNewSimOFPLightBoxVisible, isEditSimOFPLightBoxVisible } =
    useAppSelector(selectApp);
  const { simOFPFormInEdit } = useAppSelector(selectSim);
  const { userName } = useAppSelector(selectAuth);

  const [crewlistString, setCrewlistString] = useState('');
  const [errorMsg, setErrorMsg] = useState('');
  const [aircraftTypeOption, setAircraftTypeOption] = useState([]);
  const [selectTag, setSelectTag] = useState(enablePagedTemplateSelector ? 'ALL' : '');
  const [tagOptions, setTagOptions] = useState<string[]>(templateTags || []);
  const [tagPage, setTagPage] = useState(initialTemplateTagPaged.page || 1);
  const [tagLimit, setTagLimit] = useState(initialTemplateTagPaged.limit || DEFAULT_PAGE_LIMIT);
  const [tagTotalPages, setTagTotalPages] = useState(initialTemplateTagPaged.totalPages || 1);
  const [tagKeyword, setTagKeyword] = useState('');
  const [tagLoading, setTagLoading] = useState(
    enablePagedTemplateSelector && (initialTemplateTagPaged.records?.length || 0) === 0,
  );

  const [templatePage, setTemplatePage] = useState(initialTemplateUfiPaged.page || 1);
  const [templateLimit, setTemplateLimit] = useState(initialTemplateUfiPaged.limit || DEFAULT_PAGE_LIMIT);
  const [templateTotalPages, setTemplateTotalPages] = useState(initialTemplateUfiPaged.totalPages || 1);
  const [templateKeyword, setTemplateKeyword] = useState('');
  const [templateLoading, setTemplateLoading] = useState(
    enablePagedTemplateSelector && (initialTemplateUfiPaged.records?.length || 0) === 0,
  );

  const [selectedUfiLocal, setSelectedUfiLocal] = useState(simOFPFormInEdit?.ufi || '');
  const selectedUfiRef = useRef(selectedUfiLocal);

  const notSelectedTemplate = !simOFPFormInEdit?.ufi;
  const templatesUfiReady = enablePagedTemplateSelector ? true : (allTemplateUfis?.length || 0) > 0;
  const templatesTagsReady = enablePagedTemplateSelector ? true : (templateTags?.length || 0) > 0;
  const [ufiResetKey, setUfiResetKey] = useState(0);
  const EMPTY_TEMPLATE_FIELDS = {
    aircraftType: '',
    aircraftOp: '',
    aircraftReg: '',
    status: '',
    flightNum: '',
    flightNumSuffix: '',
    createdBy: '',
    createTime: '',
    simCode: '',
    crewlist: [],
    additionalInfo: {
      extraFuel: 0,
      snn: '',
      loadSheet: [],
      unsavedLoadsheet: [],
      loadSheetTemplate: [],
      digitalRefueling: [],
    },
  };
  const latestUfiRef = useRef<string>('');

  const [ufiOptions, setUfiOptions] = useState<string[]>(
    enablePagedTemplateSelector ? (initialTemplateUfiPaged.records || []) : (allTemplateUfis || []),
  );
  const latestTagRequestRef = useRef(0);
  const latestTemplateRequestRef = useRef(0);
  const tagAbortControllerRef = useRef<AbortController | null>(null);
  const templateAbortControllerRef = useRef<AbortController | null>(null);
  const didHydrateTagFromPrefetchRef = useRef(false);
  const didHydrateTemplateFromPrefetchRef = useRef(false);
  const suppressNextEmptyTagSearchRef = useRef(false);
  const tagFirstPageCacheRef = useRef<IPagedStringList>({
    records: initialTemplateTagPaged.records || [],
    page: 1,
    limit: initialTemplateTagPaged.limit || DEFAULT_PAGE_LIMIT,
    total: initialTemplateTagPaged.total || 0,
    totalPages: initialTemplateTagPaged.totalPages || 1,
  });
  const templateFirstPageCacheByTagRef = useRef<Record<string, IPagedStringList>>({
    ALL: {
      records: initialTemplateUfiPaged.records || [],
      page: 1,
      limit: initialTemplateUfiPaged.limit || DEFAULT_PAGE_LIMIT,
      total: initialTemplateUfiPaged.total || 0,
      totalPages: initialTemplateUfiPaged.totalPages || 1,
    },
  });
  const tagPageStateRef = useRef({
    options: tagOptions,
    page: tagPage,
    limit: tagLimit,
    totalPages: tagTotalPages,
  });
  const templatePageStateRef = useRef({
    options: ufiOptions,
    page: templatePage,
    limit: templateLimit,
    totalPages: templateTotalPages,
  });

  useEffect(() => {
    tagPageStateRef.current = {
      options: tagOptions,
      page: tagPage,
      limit: tagLimit,
      totalPages: tagTotalPages,
    };
  }, [tagOptions, tagPage, tagLimit, tagTotalPages]);

  useEffect(() => {
    templatePageStateRef.current = {
      options: ufiOptions,
      page: templatePage,
      limit: templateLimit,
      totalPages: templateTotalPages,
    };
  }, [ufiOptions, templatePage, templateLimit, templateTotalPages]);

  useEffect(() => {
    if (enablePagedTemplateSelector) return;
    if (!selectTag || selectTag === 'ALL') {
      setUfiOptions(allTemplateUfis || []);
    }
  }, [allTemplateUfis, selectTag, enablePagedTemplateSelector]);

  useEffect(() => {
    if (!enablePagedTemplateSelector) {
      setTagOptions(templateTags || []);
      return;
    }

    const incoming = initialTemplateTagPaged.records || [];
    const hasIncoming = incoming.length > 0;
    if ((initialTemplateTagPaged.page || 1) === 1) {
      tagFirstPageCacheRef.current = {
        records: incoming,
        page: 1,
        limit: initialTemplateTagPaged.limit || DEFAULT_PAGE_LIMIT,
        total: initialTemplateTagPaged.total || 0,
        totalPages: initialTemplateTagPaged.totalPages || 1,
      };
    }
    const localIsPristine =
      (!selectTag || selectTag === 'ALL') &&
      !tagKeyword &&
      tagPage === 1 &&
      (tagOptions.length <= 1);

    if (!hasIncoming) return;
    if (didHydrateTagFromPrefetchRef.current && !localIsPristine) return;

    didHydrateTagFromPrefetchRef.current = true;
    setTagLoading(false);
    setTagOptions(['ALL', ...incoming]);
    setTagPage(initialTemplateTagPaged.page || 1);
    setTagLimit(initialTemplateTagPaged.limit || DEFAULT_PAGE_LIMIT);
    setTagTotalPages(initialTemplateTagPaged.totalPages || 1);
  }, [
    enablePagedTemplateSelector,
    templateTags,
    initialTemplateTagPaged,
    selectTag,
    tagKeyword,
    tagPage,
    tagOptions.length,
  ]);

  useEffect(() => {
    if (!enablePagedTemplateSelector) return;

    const incoming = initialTemplateUfiPaged.records || [];
    const hasIncoming = incoming.length > 0;
    if ((initialTemplateUfiPaged.page || 1) === 1) {
      templateFirstPageCacheByTagRef.current.ALL = {
        records: incoming,
        page: 1,
        limit: initialTemplateUfiPaged.limit || DEFAULT_PAGE_LIMIT,
        total: initialTemplateUfiPaged.total || 0,
        totalPages: initialTemplateUfiPaged.totalPages || 1,
      };
    }
    const localIsPristine =
      (!selectTag || selectTag === 'ALL') &&
      !templateKeyword &&
      templatePage === 1 &&
      !selectedUfiLocal &&
      ufiOptions.length === 0;

    if (!hasIncoming) return;
    if (didHydrateTemplateFromPrefetchRef.current && !localIsPristine) return;

    didHydrateTemplateFromPrefetchRef.current = true;
    setTemplateLoading(false);
    setUfiOptions(incoming);
    setTemplatePage(initialTemplateUfiPaged.page || 1);
    setTemplateLimit(initialTemplateUfiPaged.limit || DEFAULT_PAGE_LIMIT);
    setTemplateTotalPages(initialTemplateUfiPaged.totalPages || 1);
  }, [
    enablePagedTemplateSelector,
    initialTemplateUfiPaged,
    selectTag,
    templateKeyword,
    templatePage,
    selectedUfiLocal,
    ufiOptions.length,
  ]);

  const restoreTagFirstPageFromCache = useCallback(() => {
    const cached = tagFirstPageCacheRef.current;
    latestTagRequestRef.current += 1;
    setTagLoading(false);
    setTagOptions(['ALL', ...(cached.records || [])]);
    setTagPage(1);
    setTagLimit(cached.limit || DEFAULT_PAGE_LIMIT);
    setTagTotalPages(cached.totalPages || 1);
  }, []);

  const restoreTemplateFirstPageFromCache = useCallback((tag: string) => {
    const cacheKey = normalizeTagKey(tag);
    const cached = templateFirstPageCacheByTagRef.current[cacheKey];
    if (!cached) return false;

    latestTemplateRequestRef.current += 1;
    setTemplateLoading(false);
    setUfiOptions(cached.records || []);
    setTemplatePage(1);
    setTemplateLimit(cached.limit || DEFAULT_PAGE_LIMIT);
    setTemplateTotalPages(cached.totalPages || 1);
    return true;
  }, []);

  const readyToSubmit =
    !!simOFPFormInEdit?.std &&
    !!simOFPFormInEdit?.aircraftType &&
    !!simOFPFormInEdit?.aircraftReg &&
    !!simOFPFormInEdit?.ufi &&
    !!simOFPFormInEdit?.flightNum &&
    numberOnlyInput(simOFPFormInEdit?.flightNum) &&
    !!simOFPFormInEdit?.flightNumSuffix &&
    characterOnlyInput(simOFPFormInEdit?.flightNumSuffix) &&
    simOFPFormInEdit?.crewlist?.length !== 0;

  const SimTraineeInfoListItemComponent = (crew, index) => {
    return (
      <SimTraineeInfoListItem
        key={`SimTraineeInfoListItem${index}`}
        galacxyID={crew?.galacxyId || ''}
        dutyCode={crew?.dutyCode || ''}
        onChange={(dutyCode) => {
          dispatch(setDutyCode({ galacxyId: crew.galacxyId, dutyCode }));
        }}
        onClick={() => {
          dispatch(deleteCrew({ galacxyId: crew.galacxyId }));
        }}
      />
    );
  };

  const fetchTagPage = useCallback(
    async (nextPage: number, nextLimit: number, keyword: string, append: boolean = false) => {
      if (!enablePagedTemplateSelector) return;

      const previous = tagPageStateRef.current;
      const requestId = latestTagRequestRef.current + 1;
      latestTagRequestRef.current = requestId;
      tagAbortControllerRef.current?.abort();
      const controller = new AbortController();
      tagAbortControllerRef.current = controller;
      setTagLoading(true);

      const cleanKeyword = String(keyword || '').trim();

      try {
        const res = await getSimTemplateTagList({
          page: nextPage,
          limit: nextLimit,
          ...(cleanKeyword ? { tagKeyword: cleanKeyword } : {}),
        }, { signal: controller.signal });

        if (latestTagRequestRef.current !== requestId) return;
        if (isCanceledResponse(res)) return;
        if (isFailedResponse(res)) {
          throw res;
        }

        const normalized = normalizePagedStringList(res, {
          ...DEFAULT_PAGED_STRING_LIST,
          page: nextPage,
          limit: nextLimit,
        });

        if (append && normalized.page > 1) {
          setTagOptions((prev) => {
            const prevWithoutAll = (Array.isArray(prev) ? prev : []).filter((v) => v !== 'ALL');
            return ['ALL', ...mergeUniqueStrings(prevWithoutAll, normalized.records)];
          });
        } else {
          setTagOptions(['ALL', ...normalized.records]);
        }
        setTagPage(normalized.page);
        setTagLimit(normalized.limit);
        setTagTotalPages(normalized.totalPages);
        if (!cleanKeyword && normalized.page === 1) {
          tagFirstPageCacheRef.current = {
            records: normalized.records,
            page: 1,
            limit: normalized.limit,
            total: normalized.total,
            totalPages: normalized.totalPages,
          };
        }
      } catch (err) {
        if (latestTagRequestRef.current !== requestId) return;
        if (isCanceledResponse(err)) return;
        console.log(err);
        setTagOptions(previous.options || []);
        setTagPage(previous.page || 1);
        setTagLimit(previous.limit || DEFAULT_PAGE_LIMIT);
        setTagTotalPages(previous.totalPages || 1);
      } finally {
        if (latestTagRequestRef.current === requestId) {
          setTagLoading(false);
        }
        if (tagAbortControllerRef.current === controller) {
          tagAbortControllerRef.current = null;
        }
      }
    },
    [enablePagedTemplateSelector],
  );

  const fetchTemplatePage = useCallback(
    async (nextPage: number, nextLimit: number, nextTag: string, keyword: string, append: boolean = false) => {
      if (!enablePagedTemplateSelector) return;

      const previous = templatePageStateRef.current;
      const requestId = latestTemplateRequestRef.current + 1;
      latestTemplateRequestRef.current = requestId;
      templateAbortControllerRef.current?.abort();
      const controller = new AbortController();
      templateAbortControllerRef.current = controller;
      setTemplateLoading(true);

      const cleanTag = String(nextTag || '').trim();
      const apiTag = cleanTag && cleanTag !== 'ALL' ? cleanTag : '';
      const cleanKeyword = String(keyword || '').trim();

      try {
        const res = await getSimTemplateUfiList({
          page: nextPage,
          limit: nextLimit,
          ...(apiTag ? { tag: apiTag } : {}),
          ...(cleanKeyword ? { templateKeyword: cleanKeyword } : {}),
        }, { signal: controller.signal });

        if (latestTemplateRequestRef.current !== requestId) return;
        if (isCanceledResponse(res)) return;
        if (isFailedResponse(res)) {
          throw res;
        }

        const normalized = normalizePagedStringList(res, {
          ...DEFAULT_PAGED_STRING_LIST,
          page: nextPage,
          limit: nextLimit,
        });

        if (append && normalized.page > 1) {
          setUfiOptions((prev) => mergeUniqueStrings(Array.isArray(prev) ? prev : [], normalized.records));
        } else {
          setUfiOptions(normalized.records);
        }
        setTemplatePage(normalized.page);
        setTemplateLimit(normalized.limit);
        setTemplateTotalPages(normalized.totalPages);
        if (!cleanKeyword && normalized.page === 1) {
          templateFirstPageCacheByTagRef.current[normalizeTagKey(cleanTag)] = {
            records: normalized.records,
            page: 1,
            limit: normalized.limit,
            total: normalized.total,
            totalPages: normalized.totalPages,
          };
        }
      } catch (err) {
        if (latestTemplateRequestRef.current !== requestId) return;
        if (isCanceledResponse(err)) return;
        console.log(err);
        setUfiOptions(previous.options || []);
        setTemplatePage(previous.page || 1);
        setTemplateLimit(previous.limit || DEFAULT_PAGE_LIMIT);
        setTemplateTotalPages(previous.totalPages || 1);
      } finally {
        if (latestTemplateRequestRef.current === requestId) {
          setTemplateLoading(false);
        }
        if (templateAbortControllerRef.current === controller) {
          templateAbortControllerRef.current = null;
        }
      }
    },
    [enablePagedTemplateSelector],
  );

  const onChangeTag = (tag) => {
    const nextTag = (tag || 'ALL').trim() || 'ALL';
    const normalizedCurrentTag = (selectTag || 'ALL').trim() || 'ALL';

    if (
      enablePagedTemplateSelector &&
      nextTag === normalizedCurrentTag &&
      !templateKeyword &&
      templatePage === 1
    ) {
      return;
    }

    setSelectTag(nextTag);

    if (enablePagedTemplateSelector) {
      suppressNextEmptyTagSearchRef.current = true;
      setTemplateKeyword('');
      const restored = restoreTemplateFirstPageFromCache(nextTag);
      if (!restored) {
        fetchTemplatePage(1, templateLimit, nextTag, '', false);
      }
      setTagKeyword('');
      restoreTagFirstPageFromCache();
    } else {
      if (nextTag === 'ALL') {
        setUfiOptions(allTemplateUfis || []);
      } else {
        setUfiOptions(tagUfiMap?.[nextTag] || []);
      }
    }

    setSelectedUfiLocal('');
    selectedUfiRef.current = '';
    latestUfiRef.current = '';

    setUfiResetKey((k) => k + 1);

    dispatch(
      setSimOFPFormInEdit({
        aircraftType: '',
        aircraftOp: '',
        aircraftReg: '',
        tag: nextTag === 'ALL' ? '' : nextTag,
        ufi: '',
        status: '',
        flightNum: '',
        flightNumSuffix: '',
        createdBy: '',
        createTime: '',
        simCode: '',
        crewlist: [],
        templateName: '',
        additionalInfo: {
          extraFuel: 0,
          snn: '',
          loadSheet: [],
          unsavedLoadsheet: [],
          loadSheetTemplate: [],
          digitalRefueling: [],
        },
      }),
    );
  };

  const getAircraftTypeOptions = useCallback(async () => {
    const response = await getAircraftTypes();
    if (!!response) {
      const optionDatas = response.aircraftTypes.map((aircraftType) => ({ value: aircraftType }));
      setAircraftTypeOption(optionDatas);
    } else {
      console.log(`Failed to load Aircraft Type Option Data`);
    }
  }, []);

  const loadTemplateByUfi = useCallback(
    async (ufi: string) => {
      latestUfiRef.current = ufi;

      try {
        const res = await getSimPlanTemplates({ ufi, limit: 10 });

        if (latestUfiRef.current !== ufi) return;

        const records = res?.data?.records || res?.records || [];
        const template = Array.isArray(records) ? records[0] : null;

        if (!template) {
          dispatch(setSimOFPFormInEdit({ ufi, ...EMPTY_TEMPLATE_FIELDS }));
          return;
        }

        let flightNum = template.flightNum;
        flightNum = flightNum?.toString().padStart(3, '0');
        if (flightNum?.length > 3) flightNum = flightNum.substr(flightNum.length - 3);

        let flightNumSuffix = (template.flightNumSuffix || '').toUpperCase();
        if (flightNumSuffix.length > 1) flightNumSuffix = flightNumSuffix.substr(flightNumSuffix.length - 1);

        dispatch(
          setSimOFPFormInEdit({
            ...EMPTY_TEMPLATE_FIELDS,

            aircraftType: template.aircraftType || '',
            aircraftOp: template.aircraftOp || '',
            aircraftReg: template.aircraftReg || '',
            ufi: template.ufi || ufi,
            status: template.status || '',
            flightNum: flightNum || '',
            flightNumSuffix: flightNumSuffix || '',
            createdBy: template.createdBy || '',
            createTime: template.createTime || '',
            simCode: template.simCode || '',
            crewlist: template.crewlist || [],
            additionalInfo: template.additionalInfo || EMPTY_TEMPLATE_FIELDS.additionalInfo,
          }),
        );
      } catch (e) {
        console.log(e);

        if (latestUfiRef.current !== ufi) return;
        dispatch(setSimOFPFormInEdit({ ufi, ...EMPTY_TEMPLATE_FIELDS }));
      }
    },
    [dispatch],
  );

  const selectAircraftType = (option) => {
    const selectedOption = option.split('-');
    const aircraftReg = selectedOption[0];
    const aircraftType = selectedOption[1];
    dispatch(setSimOFPFormInEdit({ aircraftReg, aircraftType }));
  };

  const addSimTrainee = () => {
    const processedCrewlistString = crewlistString.replaceAll(/ +/g, '').toUpperCase();
    dispatch(addCrews({ crewlistString: processedCrewlistString }));
    setCrewlistString('');
  };

  const changeFlightNumber = (flightNum) => {
    let checkedFlightNum: string = flightNum;
    checkedFlightNum = checkedFlightNum.padStart(3, '0');
    if (flightNum.length > 3) {
      checkedFlightNum = flightNum.substr(flightNum.length - 3);
    }
    dispatch(setSimOFPFormInEdit({ flightNum: checkedFlightNum }));
  };

  const crewListDuplicateChecker = useCallback(() => {
    const processedCrewlist = crewlistString.replaceAll(/ +/g, '').toUpperCase().split(',');
    const currentCrewlist = simOFPFormInEdit.crewlist.map((crew) => crew.galacxyId);
    return processedCrewlist.filter((crew) => currentCrewlist.includes(crew)).length !== 0;
  }, [crewlistString, simOFPFormInEdit.crewlist]);

  const changeFlightNumberSuffix = (flightNumSuffix) => {
    let checkedFlightNumSuffix: string = flightNumSuffix;
    if (flightNumSuffix.length > 1) {
      checkedFlightNumSuffix = flightNumSuffix.substr(flightNumSuffix.length - 1);
    }
    dispatch(setSimOFPFormInEdit({ flightNumSuffix: checkedFlightNumSuffix.toUpperCase() }));
  };

  const clearForm = useCallback(() => {
    latestTagRequestRef.current += 1;
    latestTemplateRequestRef.current += 1;
    tagAbortControllerRef.current?.abort();
    templateAbortControllerRef.current?.abort();
    tagAbortControllerRef.current = null;
    templateAbortControllerRef.current = null;
    suppressNextEmptyTagSearchRef.current = false;
    setTagLoading(enablePagedTemplateSelector && (initialTemplateTagPaged.records?.length || 0) === 0);
    setTemplateLoading(enablePagedTemplateSelector && (initialTemplateUfiPaged.records?.length || 0) === 0);

    dispatch(clearSimOFPForm());
    setErrorMsg('');

    setSelectTag(enablePagedTemplateSelector ? 'ALL' : '');
    setTagKeyword('');
    setTemplateKeyword('');

    if (enablePagedTemplateSelector) {
      tagFirstPageCacheRef.current = {
        records: initialTemplateTagPaged.records || [],
        page: 1,
        limit: initialTemplateTagPaged.limit || DEFAULT_PAGE_LIMIT,
        total: initialTemplateTagPaged.total || 0,
        totalPages: initialTemplateTagPaged.totalPages || 1,
      };
      templateFirstPageCacheByTagRef.current = {
        ALL: {
          records: initialTemplateUfiPaged.records || [],
          page: 1,
          limit: initialTemplateUfiPaged.limit || DEFAULT_PAGE_LIMIT,
          total: initialTemplateUfiPaged.total || 0,
          totalPages: initialTemplateUfiPaged.totalPages || 1,
        },
      };

      setTagOptions(['ALL', ...(initialTemplateTagPaged.records || [])]);
      setTagPage(initialTemplateTagPaged.page || 1);
      setTagLimit(initialTemplateTagPaged.limit || DEFAULT_PAGE_LIMIT);
      setTagTotalPages(initialTemplateTagPaged.totalPages || 1);

      setUfiOptions(initialTemplateUfiPaged.records || []);
      setTemplatePage(initialTemplateUfiPaged.page || 1);
      setTemplateLimit(initialTemplateUfiPaged.limit || DEFAULT_PAGE_LIMIT);
      setTemplateTotalPages(initialTemplateUfiPaged.totalPages || 1);
    } else {
      setTagOptions(templateTags || []);
      setUfiOptions(allTemplateUfis || []);
    }

    setSelectedUfiLocal('');
    selectedUfiRef.current = '';
    latestUfiRef.current = '';

    setUfiResetKey((k) => k + 1);
    dispatch(setSimOFPFormInEdit({ tag: '', ufi: '' }));
  }, [
    dispatch,
    allTemplateUfis,
    templateTags,
    enablePagedTemplateSelector,
    initialTemplateTagPaged,
    initialTemplateUfiPaged,
  ]);

  const onTagSearchKeywordChange = useCallback((keyword: string) => {
    if (!enablePagedTemplateSelector) return;
    const nextKeyword = String(keyword || '').trim();
    if (nextKeyword === '' && suppressNextEmptyTagSearchRef.current) {
      suppressNextEmptyTagSearchRef.current = false;
      return;
    }
    if (nextKeyword === '') {
      if (tagKeyword === '') return;
      setTagKeyword('');
      restoreTagFirstPageFromCache();
      return;
    }
    if (nextKeyword !== '' && suppressNextEmptyTagSearchRef.current) {
      suppressNextEmptyTagSearchRef.current = false;
    }

    if (nextKeyword === tagKeyword) return;

    setTagKeyword(nextKeyword);
    fetchTagPage(1, tagLimit, nextKeyword, false);
  }, [
    enablePagedTemplateSelector,
    fetchTagPage,
    tagLimit,
    tagKeyword,
    restoreTagFirstPageFromCache,
  ]);

  const onTemplateSearchKeywordChange = useCallback((keyword: string) => {
    if (!enablePagedTemplateSelector) return;
    const nextKeyword = String(keyword || '').trim();
    if (nextKeyword === '') {
      if (templateKeyword === '') return;
      setTemplateKeyword('');
      restoreTemplateFirstPageFromCache(selectTag || 'ALL');
      return;
    }
    if (nextKeyword === templateKeyword) return;

    setTemplateKeyword(nextKeyword);
    fetchTemplatePage(1, templateLimit, selectTag || 'ALL', nextKeyword, false);
  }, [
    enablePagedTemplateSelector,
    fetchTemplatePage,
    templateLimit,
    selectTag,
    templateKeyword,
    restoreTemplateFirstPageFromCache,
  ]);

  const onTagReachListEnd = useCallback(() => {
    if (!enablePagedTemplateSelector) return;
    if (tagLoading) return;
    if (tagPage >= tagTotalPages) return;
    fetchTagPage(tagPage + 1, tagLimit, tagKeyword, true);
  }, [
    enablePagedTemplateSelector,
    tagLoading,
    tagPage,
    tagTotalPages,
    tagLimit,
    tagKeyword,
    fetchTagPage,
  ]);

  const onTemplateReachListEnd = useCallback(() => {
    if (!enablePagedTemplateSelector) return;
    if (templateLoading) return;
    if (templatePage >= templateTotalPages) return;
    fetchTemplatePage(templatePage + 1, templateLimit, selectTag || 'ALL', templateKeyword, true);
  }, [
    enablePagedTemplateSelector,
    templateLoading,
    templatePage,
    templateTotalPages,
    templateLimit,
    selectTag,
    templateKeyword,
    fetchTemplatePage,
  ]);

  const onTagDropdownVisibleChange = useCallback((visible: boolean) => {
    if (!enablePagedTemplateSelector || visible) return;
    if (!tagKeyword) return;
    setTagKeyword('');
    restoreTagFirstPageFromCache();
  }, [enablePagedTemplateSelector, tagKeyword, restoreTagFirstPageFromCache]);

  const onTemplateDropdownVisibleChange = useCallback((visible: boolean) => {
    if (!enablePagedTemplateSelector || visible) return;
    if (!templateKeyword) return;
    setTemplateKeyword('');
    restoreTemplateFirstPageFromCache(selectTag || 'ALL');
  }, [
    enablePagedTemplateSelector,
    templateKeyword,
    selectTag,
    restoreTemplateFirstPageFromCache,
  ]);

  const submitNewSimOFP = () => {
    setErrorMsg('');
    const newSimOFPForm = {
      ...simOFPFormInEdit,
      createTime: moment().toISOString(),
      createdBy: userName,
    };
    postNewSimPlanUpdate(newSimOFPForm)
      .then((res) => {
        if (res.status === 500) throw res;
        if (!!postAction) postAction(res);
        clearForm();
      })
      .catch((err) => {
        if (err.status === 500 && err.detail === 'Duplicate ufi') {
          setErrorMsg('This ufi is already existed in database. Please use other suffix.');
        } else {
          console.log(err);
        }
      });

  };

  useEffect(() => {
    if (!isEdit && isAddNewSimOFPLightBoxVisible) {
      clearForm();
    }
  }, [isAddNewSimOFPLightBoxVisible, isEdit, clearForm]);

  useEffect(() => () => {
    tagAbortControllerRef.current?.abort();
    templateAbortControllerRef.current?.abort();
    tagAbortControllerRef.current = null;
    templateAbortControllerRef.current = null;
  }, []);

  return (
    <CXLightBox
      visible={isEdit ? isEditSimOFPLightBoxVisible : isAddNewSimOFPLightBoxVisible}
      TriggerButton={TriggerButton}
      heading={heading}
      submitButtonText={submitButtonText}
      submitButtonDisabled={!readyToSubmit}
      setVisible={(visible) => {
        if (!visible) {
          latestTagRequestRef.current += 1;
          latestTemplateRequestRef.current += 1;
          tagAbortControllerRef.current?.abort();
          templateAbortControllerRef.current?.abort();
          tagAbortControllerRef.current = null;
          templateAbortControllerRef.current = null;
          setTagLoading(false);
          setTemplateLoading(false);
          setUfiResetKey((k) => k + 1);
        }

        dispatch(
          isEdit
            ? setAppFlags({ field: 'isEditSimOFPLightBoxVisible', value: visible })
            : setAppFlags({ field: 'isAddNewSimOFPLightBoxVisible', value: visible }),
        );
      }}
      submit={() => submitNewSimOFP()}
      clear={() => {
        if (!isEdit) clearForm();
      }}
    >
      {/* -------- Section 1: Template + Aircraft type -------- */}
      <div style={rowStyle}>
        <CRow>
          <CCol xs={6}></CCol>

          <CCol xs={3} className="sim-tag-col" style={{ paddingTop: '4px' }}>
            <div className={`sim-tag-select ${selectTag ? 'has-value' : ''}`}>
              <SingleSelect
                key={`tag-${ufiResetKey}`}
                options={enablePagedTemplateSelector ? tagOptions : templateTags}
                value={selectTag}
                zIndex={1060}
                disabled={!templatesTagsReady}
                loading={enablePagedTemplateSelector ? tagLoading : false}
                placeholder={templatesTagsReady ? 'Tag' : 'Loading tags...'}
                maxOptions={25}
                maxResults={200}
                debounceMs={enablePagedTemplateSelector ? PAGED_SELECT_DEBOUNCE_MS : 500}
                resetKey={ufiResetKey}
                serverSideSearch={enablePagedTemplateSelector}
                onSearchKeywordChange={onTagSearchKeywordChange}
                onDropdownVisibleChange={onTagDropdownVisibleChange}
                infiniteScroll={enablePagedTemplateSelector}
                hasMore={enablePagedTemplateSelector ? tagPage < tagTotalPages : false}
                onReachListEnd={onTagReachListEnd}
                onSelectOption={(tag) => {
                  const next = (tag || '').trim();
                  onChangeTag(next || 'ALL');
                }}
              />
            </div>
          </CCol>
        </CRow>

        <CRow>
          <CCol xs={6} style={{ paddingTop: '2px' }}>
            <div className={'CathaySansMd'} style={{ fontSize: '18px', lineHeight: '22px' }}>
              Aircraft type and flight plan template
            </div>
          </CCol>

          <CCol xs={4} className="sim-ufi-col" style={{ paddingRight: '0px', paddingTop: '4px' }}>
            <div className={`sim-ufi-multiselect ${selectedUfiLocal ? 'has-value' : ''}`}>

              <SingleSelect
                key={`ufi-${ufiResetKey}`}
                options={ufiOptions}
                value={selectedUfiLocal}
                zIndex={1080}
                disabled={!templatesUfiReady}
                loading={enablePagedTemplateSelector ? templateLoading : false}
                placeholder={templatesUfiReady ? 'Select template' : 'Loading templates...'}
                searchPlaceholder={
                  enablePagedTemplateSelector
                    ? 'Type Flight No. / Orig / Dest for more records'
                    : ''
                }
                maxOptions={25}
                maxResults={200}
                debounceMs={enablePagedTemplateSelector ? PAGED_SELECT_DEBOUNCE_MS : 500}
                resetKey={ufiResetKey}
                serverSideSearch={enablePagedTemplateSelector}
                onSearchKeywordChange={onTemplateSearchKeywordChange}
                onDropdownVisibleChange={onTemplateDropdownVisibleChange}
                infiniteScroll={enablePagedTemplateSelector}
                hasMore={enablePagedTemplateSelector ? templatePage < templateTotalPages : false}
                onReachListEnd={onTemplateReachListEnd}
                onSelectOption={(ufi) => {
                  if (!ufi) {
                    if (selectedUfiRef.current) {
                      setSelectedUfiLocal('');
                      selectedUfiRef.current = '';
                      dispatch(
                        setSimOFPFormInEdit({
                          ufi: '',
                          ...EMPTY_TEMPLATE_FIELDS,
                        }),
                      );
                      latestUfiRef.current = '';
                      setUfiResetKey((k) => k + 1);
                    }
                    return;
                  }

                  if (ufi === selectedUfiRef.current) return;

                  setSelectedUfiLocal(ufi);
                  selectedUfiRef.current = ufi;

                  dispatch(setSimOFPFormInEdit({ ufi, ...EMPTY_TEMPLATE_FIELDS }));
                  if (enablePagedTemplateSelector && templateKeyword) {
                    setTemplateKeyword('');
                    const restored = restoreTemplateFirstPageFromCache(selectTag || 'ALL');
                    if (!restored) {
                      fetchTemplatePage(1, templateLimit, selectTag || 'ALL', '', false);
                    }
                  }
                  loadTemplateByUfi(ufi);
                }}
              />
            </div>
          </CCol>

          <CCol xs={2} className="sim-aircraft-col" style={{ paddingRight: '0px', paddingTop: '4px' }}>
            <CXDropDown
              zIndex={1070}
              value={
                simOFPFormInEdit?.aircraftReg && simOFPFormInEdit.aircraftType
                  ? `${simOFPFormInEdit.aircraftReg}-${simOFPFormInEdit.aircraftType}`
                  : ''
              }
              placeholder={'Aircraft type'}
              preAction={() => dispatch(setAppFlags({ field: 'isBackgroundLoading', value: true }))}
              postAction={() => dispatch(setAppFlags({ field: 'isBackgroundLoading', value: false }))}
              fetchData={getAircraftTypeOptions}
              onChange={(option) => selectAircraftType(option)}
              noOptionMsg={'Loading...'}
              options={aircraftTypeOption}
            />
          </CCol>
        </CRow>
      </div>

      <hr className={'m-0'} />

      <div style={rowStyle}>
        <CRow>
          <CCol xs={6}>
            <div className={"CathaySansMd"} style={{ fontSize: "18px", lineHeight: "22px" }}>STD and Flight number</div>
          </CCol>
          <CCol xs={6}>
            <CRow>
              <CCol xs={6}>
                <CXDatePicker
                  placeholder={"STD"}
                  value={simOFPFormInEdit?.std}
                  locale={"en-GB"}
                  onChange={(date) => {
                    dispatch(setSimOFPFormInEdit({ std: date }))
                  }}
                  timepicker={true}
                  preserveUtcTime={true}
                />
              </CCol>
              <CCol xs={4}>
                <CXTextInput
                  disabled={notSelectedTemplate}
                  value={simOFPFormInEdit?.flightNum || ""}
                  additionalValidation={numberOnlyInput}
                  errMsg={"Number Only"}
                  placeholder={"Flight Number"}
                  onChange={(flightNum) => changeFlightNumber(flightNum)}
                />
              </CCol>
              <CCol xs={2}>
                <CXTextInput
                  disabled={notSelectedTemplate}
                  value={simOFPFormInEdit?.flightNumSuffix || ""}
                  additionalValidation={characterOnlyInput}
                  errMsg={"Character Only"}
                  placeholder={"Suffix"}
                  onChange={(flightNumSuffix) => changeFlightNumberSuffix(flightNumSuffix)}
                />
              </CCol>
            </CRow>

            {!!errorMsg && (
              <CRow>
                <CCol><div className={'error-message'}>{errorMsg}</div></CCol>
              </CRow>
            )}
          </CCol>
        </CRow>
      </div>

      <hr className={"m-0"} />

      <div style={rowStyle}>
        <CRow>
          <CCol xs={6}>
            <div className={"CathaySansMd"} style={{ fontSize: "18px", lineHeight: "22px" }}>
              Add GalaCXy ID of required trainee here
            </div>
          </CCol>
          <CCol xs={6}>
            <CRow>
              <CCol xs={12}>
                <div className={"cx-form-helper"}>
                  Please input the GalaCXy ID here and separate each by a comma ‘,’.
                </div>
              </CCol>
            </CRow>
            <CRow>
              <CCol xs={9}>
                <CXTextInput
                  disabled={notSelectedTemplate}
                  value={crewlistString}
                  placeholder={"For example CRWABCD,CRWDEF,CRWGHI"}
                  onChange={setCrewlistString}
                  hasLabel={false}
                  additionalValidation={() => !crewListDuplicateChecker()}
                  errMsg={"Could not add duplicated trainee"}
                />
              </CCol>
              <CCol xs={3}>
                <CXButton
                  text={"Add"}
                  width={"100%"}
                  disabled={!crewlistString || crewListDuplicateChecker()}
                  onClick={() => addSimTrainee()}
                />
              </CCol>
            </CRow>

            {!!simOFPFormInEdit?.crewlist?.length && (
              <>
                <CRow>
                  <CCol xs={9}>
                    <div style={{ fontFamily: "CathaySans_Rg", fontStyle: "normal", fontWeight: 400, fontSize: "12px", lineHeight: "16px" }}>
                      Trainee
                    </div>
                  </CCol>
                  <CCol xs={3}>
                    <div style={{ fontFamily: "CathaySans_Rg", fontStyle: "normal", fontWeight: 400, fontSize: "12px", lineHeight: "16px" }}>
                      Duty code
                    </div>
                  </CCol>
                </CRow>

                {simOFPFormInEdit?.crewlist.map((crew, index) => {
                  if (index > 0) {
                    return (
                      <div key={`SimTraineeInfoListItemComponentBlock${index}`}>
                        <hr className={"m-0"} />
                        {SimTraineeInfoListItemComponent(crew, index)}
                      </div>
                    );
                  }
                  return (
                    <div key={`SimTraineeInfoListItemOnlyOneComponentBlock${index}`}>
                      {SimTraineeInfoListItemComponent(crew, index)}
                    </div>
                  );
                })}
              </>
            )}
          </CCol>
        </CRow>
      </div>

      <hr className={"m-0"} />

      <div style={rowStyle}>
        <CRow>
          <CCol xs={6}>
            <div className={"CathaySansMd"} style={{ fontSize: "18px", lineHeight: "22px" }}>Additional info</div>
          </CCol>
          <CCol xs={6}>
            <CRow>
              <CCol xs={4}>
                <CXTextInput
                  disabled={notSelectedTemplate}
                  value={simOFPFormInEdit?.simCode || ""}
                  placeholder={"Sim code"}
                  onChange={(simCode) => {
                    dispatch(setSimOFPFormInEdit({ simCode }))
                  }}
                />
              </CCol>
              <CCol xs={4}>
                <CXTextInput
                  disabled={notSelectedTemplate}
                  value={(!!simOFPFormInEdit?.additionalInfo?.extraFuel) ? simOFPFormInEdit?.additionalInfo?.extraFuel.toString() : ""}
                  placeholder={"Extra fuel"}
                  label={"Extra fuel (Optional)"}
                  unit={"Kg"}
                  onChange={(extraFuel) => {
                    if (numberOnlyInputOrNull(extraFuel)) {
                      dispatch(setAdditionalInfoForm({ extraFuel: parseInt(extraFuel) }))
                    }
                  }}
                />
              </CCol>
            </CRow>

            <div style={{ marginTop: "16px" }}>
              <CRow>
                <CCol xs={12}>
                  <CXTextArea
                    disabled={notSelectedTemplate}
                    value={simOFPFormInEdit?.additionalInfo?.snn || ""}
                    placeholder={"SNN"}
                    onChange={(snn) => {
                      dispatch(setAdditionalInfoForm({ snn }))
                    }}
                  />
                </CCol>
              </CRow>
            </div>
          </CCol>
        </CRow>
      </div>
    </CXLightBox>
  );
};

export default AddOrEditOFPLightBox;
